import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth.store';
import { supabase } from '../../lib/supabase';
import { StaffRole } from '@bisa/shared';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';

interface NavItem {
  to: string;
  label: string;
  external?: boolean;
}

function superAdminNav(): NavItem[] {
  return [
    { to: '/admin/facilities', label: 'Facilities' },
    { to: '/admin/params', label: 'Global Params' },
    { to: '/admin/lookups', label: 'Lookups' },
    { to: 'https://docs.bisa.tiwaak.com', label: 'Docs', external: true },
  ];
}

function staffNav(role: StaffRole): NavItem[] {
  const items: NavItem[] = [];
  if (role === StaffRole.FRONT_DESK || role === StaffRole.PROVIDER) {
    items.push({ to: '/queue', label: 'Live Queue' });
  }
  if (role === StaffRole.ADMIN) {
    items.push({ to: '/queue', label: 'Live Queue' });
    items.push({ to: '/facility/departments', label: 'Departments' });
    items.push({ to: '/facility/services', label: 'Services' });
    items.push({ to: '/facility/staff', label: 'Staff' });
    items.push({ to: '/facility/params', label: 'Clinic Params' });
    items.push({ to: '/reports', label: 'Reports' });
  }
  return items;
}

export function AppShell() {
  const { staff, kind, signOut } = useAuthStore();
  const navigate = useNavigate();

  const [showChangePw, setShowChangePw] = useState(false);
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSuccess, setPwSuccess] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const navItems = kind === 'super_admin' ? superAdminNav() : staff ? staffNav(staff.role) : [];

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const openChangePw = () => {
    setNewPw('');
    setConfirmPw('');
    setPwError(null);
    setPwSuccess(false);
    setShowChangePw(true);
    setShowMobileMenu(false);
  };

  const handleChangePw = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPw !== confirmPw) { setPwError('Passwords do not match'); return; }
    setPwSaving(true);
    setPwError(null);
    const { error } = await supabase.auth.updateUser({ password: newPw });
    setPwSaving(false);
    if (error) { setPwError(error.message); return; }
    setPwSuccess(true);
  };


  return (
    <div className="flex h-dvh bg-gray-50 overflow-hidden">
      {/* ── Desktop sidebar ── */}
      <aside className="hidden md:flex w-56 flex-col flex-shrink-0 border-r border-gray-200 bg-white">
        <div className="border-b px-4 py-5">
          <span className="text-lg font-bold text-blue-600">BISA</span>
          <p className="mt-0.5 truncate text-xs text-gray-500">
            {kind === 'super_admin' ? 'Super Admin' : staff?.facilityName ?? ''}
          </p>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-4 space-y-0.5">
          {navItems.map((item) =>
            item.external ? (
              <a
                key={item.to}
                href={item.to}
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-md px-3 py-2 text-sm font-medium transition-colors text-gray-700 hover:bg-gray-100"
              >
                {item.label} ↗
              </a>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `block rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="border-t px-4 py-3">
          <p className="truncate text-xs text-gray-500">
            {kind === 'super_admin' ? 'Super Admin' : staff?.role}
          </p>
          <button
            onClick={openChangePw}
            className="mt-1 block text-xs text-gray-500 hover:text-gray-700"
          >
            Change password
          </button>
          <button
            onClick={handleSignOut}
            className="mt-0.5 text-xs text-red-500 hover:text-red-700"
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* ── Page content ── */}
      <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
        {/* Mobile top bar */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 flex-shrink-0">
          <span className="text-base font-bold text-blue-600">BISA</span>
          <button
            onClick={() => setShowMobileMenu((v) => !v)}
            className="text-sm text-gray-500 border border-gray-200 rounded-md px-2 py-1"
          >
            Account
          </button>
        </header>

        {/* Mobile account dropdown */}
        {showMobileMenu && (
          <div className="md:hidden absolute top-14 right-4 z-50 bg-white border border-gray-200 rounded-lg shadow-lg py-2 min-w-40">
            <p className="px-4 py-1 text-xs text-gray-400">
              {kind === 'super_admin' ? 'Super Admin' : staff?.role}
            </p>
            <p className="px-4 py-1 text-xs text-gray-500 truncate">
              {kind === 'super_admin' ? 'Super Admin' : staff?.facilityName ?? ''}
            </p>
            <hr className="my-1 border-gray-100" />
            <button
              onClick={openChangePw}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Change password
            </button>
            <button
              onClick={async () => { setShowMobileMenu(false); await handleSignOut(); }}
              className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-gray-50"
            >
              Sign out
            </button>
          </div>
        )}

        {/* Main scrollable area */}
        <main className="flex-1 overflow-y-auto pb-16 md:pb-0">
          <Outlet />
        </main>

        {/* ── Mobile bottom navigation ── */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 flex">
          {navItems.map((item) =>
            item.external ? (
              <a
                key={item.to}
                href={item.to}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-xs font-medium transition-colors min-w-0 text-gray-500"
              >
                <span className="truncate px-1 text-center leading-tight">{item.label} ↗</span>
              </a>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setShowMobileMenu(false)}
                className={({ isActive }) =>
                  `flex-1 flex flex-col items-center justify-center py-2 gap-0.5 text-xs font-medium transition-colors min-w-0 ${
                    isActive ? 'text-blue-600' : 'text-gray-500'
                  }`
                }
              >
                <span className="truncate px-1 text-center leading-tight">{item.label}</span>
              </NavLink>
            )
          )}
        </nav>
      </div>

      {/* Change Password modal */}
      <Modal
        open={showChangePw}
        title="Change Password"
        onClose={() => setShowChangePw(false)}
        footer={
          pwSuccess ? (
            <Button onClick={() => setShowChangePw(false)}>Done</Button>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setShowChangePw(false)}>Cancel</Button>
              <Button form="change-pw-form" type="submit" loading={pwSaving}>Update Password</Button>
            </>
          )
        }
      >
        {pwSuccess ? (
          <p className="rounded-md bg-green-50 px-4 py-3 text-sm text-green-800">
            Password updated successfully.
          </p>
        ) : (
          <form id="change-pw-form" onSubmit={handleChangePw} className="space-y-4">
            <Input
              type="password"
              label="New password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              placeholder="••••••••"
              required
              minLength={8}
              autoComplete="new-password"
            />
            <Input
              type="password"
              label="Confirm new password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="new-password"
            />
            {pwError && <p className="text-sm text-red-600">{pwError}</p>}
          </form>
        )}
      </Modal>
    </div>
  );
}
